import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button, Card, Collapse, Input, Result, Tag, Typography } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { isHandledAuthError, municipalitiesApi } from '@/services'
import { useToast } from '@/widgets/common'
import type { MunicipalityResponse, OcrSystemKeyCatalogResponse } from '@/services/municipalities/types'

const { Title, Text } = Typography

const DISPLAY_ONLY_SYSTEM_KEY_PREFIX = 'ocr_display.'
// 申出書マッピングは application 系（applications / application_members / application_additions）が主。
// recipient 系は固定転写の上書き用に許容する。
const ALLOWED_SYSTEM_KEY_PREFIXES = [
  'application.',
  'application_member.',
  'application_addition.',
  'recipient.',
  'recipient_detail.',
  DISPLAY_ONLY_SYSTEM_KEY_PREFIX,
  'internal.',
] as const

type OcrFieldMapEntry = {
  systemKey?: string
  ocrPath?: string
  section?: string
  arrayPath?: string
}

type OcrPathExample = { label: string; path: string }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const textOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined

const normalizeFieldMapEntry = (value: unknown): OcrFieldMapEntry | null => {
  if (typeof value === 'string' && value.trim() !== '') {
    return { systemKey: value }
  }
  if (!isRecord(value)) return null

  const systemKey = textOrUndefined(value.systemKey)
  if (!systemKey) return null

  return {
    systemKey,
    ocrPath: textOrUndefined(value.ocrPath),
    section: textOrUndefined(value.section),
    arrayPath: textOrUndefined(value.arrayPath),
  }
}

const isDisplayOnlyEntry = (entry: OcrFieldMapEntry) =>
  !!entry.systemKey && entry.systemKey.startsWith(DISPLAY_ONLY_SYSTEM_KEY_PREFIX)

const hasAllowedSystemKeyPrefix = (systemKey: string) =>
  ALLOWED_SYSTEM_KEY_PREFIXES.some((prefix) => systemKey.startsWith(prefix))

const findInvalidSystemKey = (parsed: unknown): string | null => {
  if (!isRecord(parsed) || !isRecord(parsed.fieldMap)) return null
  for (const value of Object.values(parsed.fieldMap)) {
    const entry = normalizeFieldMapEntry(value)
    if (!entry?.systemKey) continue
    if (!hasAllowedSystemKeyPrefix(entry.systemKey)) return entry.systemKey
  }
  return null
}

const compactEntry = (entry: OcrFieldMapEntry): OcrFieldMapEntry => {
  const compact: OcrFieldMapEntry = { systemKey: entry.systemKey }
  if (entry.ocrPath) compact.ocrPath = entry.ocrPath
  if (entry.section) compact.section = entry.section
  if (entry.arrayPath) compact.arrayPath = entry.arrayPath
  return compact
}

const buildMappingHelp = (raw: string): { fieldMapSample: string; ocrPaths: OcrPathExample[] } => {
  try {
    const parsed: unknown = JSON.parse(raw || '{}')
    if (!isRecord(parsed) || !isRecord(parsed.fieldMap)) {
      return { fieldMapSample: JSON.stringify({ version: 2, fieldMap: {} }, null, 2), ocrPaths: [] }
    }

    const fieldMap: Record<string, OcrFieldMapEntry> = {}
    const ocrPaths: OcrPathExample[] = []
    const seenPaths = new Set<string>()

    Object.entries(parsed.fieldMap).forEach(([label, value]) => {
      const entry = normalizeFieldMapEntry(value)
      if (!entry || isDisplayOnlyEntry(entry)) return

      fieldMap[label] = compactEntry(entry)
      if (entry.ocrPath && !seenPaths.has(entry.ocrPath)) {
        seenPaths.add(entry.ocrPath)
        ocrPaths.push({ label, path: entry.ocrPath })
      }
    })

    return {
      fieldMapSample: JSON.stringify({ version: 2, fieldMap }, null, 2),
      ocrPaths,
    }
  } catch {
    return { fieldMapSample: JSON.stringify({ version: 2, fieldMap: {} }, null, 2), ocrPaths: [] }
  }
}

const formatJson = (raw: string | null | undefined): string => {
  if (!raw || raw.trim() === '') {
    return '{}'
  }
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** 申出書（別添様式4）用の OCR マッピング設定画面。保護台帳用 MunicipalityOcrMapping の申出書版。 */
const MunicipalityMoushideshoOcrMapping = () => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { code: codeParam } = useParams<{ code?: string }>()
  const code = codeParam ?? null

  const [municipality, setMunicipality] = useState<MunicipalityResponse | null>(null)
  const [jsonText, setJsonText] = useState<string>('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [catalog, setCatalog] = useState<OcrSystemKeyCatalogResponse[]>([])
  const [catalogFilter, setCatalogFilter] = useState('')

  useEffect(() => {
    // code の有無でマッピング定義を取得する正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!code) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    /* eslint-enable react-hooks/set-state-in-effect */
    municipalitiesApi
      .list()
      .then(({ data }) => {
        if (cancelled) return
        const found = data.find((item) => item.code === code) ?? null
        setMunicipality(found)
        if (found) setJsonText(formatJson(found.moushideshoOcrMappingJson))
      })
      .catch((err) => {
        if (isHandledAuthError(err)) return
        if (!cancelled) setError('申出書OCRマッピングの読み込みに失敗しました。')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [code])

  useEffect(() => {
    let cancelled = false
    municipalitiesApi
      .systemKeyCatalog('MOUSHIDESHO')
      .then(({ data }) => {
        if (!cancelled) setCatalog(data)
      })
      .catch((err) => {
        if (isHandledAuthError(err)) return
      })
    return () => {
      cancelled = true
    }
  }, [])

  const goToBasicInfoEdit = () => {
    if (!code) return
    navigate('municipality-edit', { code })
  }

  const filteredCatalog = useMemo(() => {
    const q = catalogFilter.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter(
      (e) =>
        e.systemKey.toLowerCase().includes(q) ||
        (e.defaultLabel && e.defaultLabel.toLowerCase().includes(q)) ||
        (e.column && e.column.toLowerCase().includes(q)),
    )
  }, [catalog, catalogFilter])

  const ocrUseDisabled = useMemo(() => municipality != null && !municipality.ocrUse, [municipality])
  const mappingHelp = useMemo(() => buildMappingHelp(jsonText), [jsonText])

  const copyToClipboard = (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text).then(() => toast.success(`コピーしました: ${text}`))
    }
  }

  const save = async () => {
    if (!municipality) return
    let compact: string
    let parsed: unknown
    try {
      parsed = JSON.parse(jsonText)
      compact = JSON.stringify(parsed)
    } catch {
      setParseError('JSONの形式が正しくありません。括弧・カンマ・引用符を確認してください。')
      return
    }
    const invalidSystemKey = findInvalidSystemKey(parsed)
    if (invalidSystemKey) {
      setParseError(`systemKey の prefix は application. / application_member. / application_addition. / recipient. / recipient_detail. / ocr_display. / internal. のいずれかにしてください: ${invalidSystemKey}`)
      return
    }
    setParseError(null)
    setSaving(true)
    try {
      const { data } = await municipalitiesApi.updateMoushideshoOcrMapping(municipality.id, { ocrMappingJson: compact })
      setMunicipality(data)
      setJsonText(formatJson(data.moushideshoOcrMappingJson))
      toast.success('申出書OCRマッピング（JSON）を保存しました。')
      goToBasicInfoEdit()
    } catch (err) {
      if (isHandledAuthError(err)) return
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbs = [
    { label: 'システム管理' },
    { label: '自治体管理', page: 'municipality-management' as const },
    { label: '申出書OCRマッピング設定' },
  ]

  if (!code) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={breadcrumbs} />
        <div className="mx-auto max-w-lg px-6 py-16">
          <Result status="warning" title="自治体コードが指定されていません。" />
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={breadcrumbs} />
        <div className="mx-auto max-w-lg px-6 py-16 text-center text-gray-600">Loading...</div>
      </div>
    )
  }

  if (!municipality) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={breadcrumbs} />
        <div className="mx-auto max-w-lg px-6 py-16">
          <Result status="404" title="自治体が見つかりませんでした。" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={breadcrumbs} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <BackButton onClick={goToBasicInfoEdit} />
        <Card>
          <Title level={4} className="!mb-1">
            申出書OCRマッピング設定（JSON）
          </Title>
          <Text type="secondary" className="block">
            <span className="font-mono font-medium text-gray-800">{municipality.code}</span>
            <span className="mx-2 text-gray-300">|</span>
            {municipality.name}
          </Text>
          <p className="mt-2 text-xs text-gray-500">
            申出書（別添様式4）の OCR が読み取ったラベル名と、システム側の項目キー（例: application.applicant_name）の対応を JSON の{' '}
            <code className="rounded bg-gray-100 px-1">fieldMap</code> などで定義します。保護台帳用の OCR マッピングとは独立した設定です。
          </p>
          <div className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <p>systemKey の prefix は以下の 7 種類。</p>
            <ul className="mt-1 list-disc pl-5">
              <li><span className="font-mono">application.*</span>: applications（申出書）テーブルへ転記</li>
              <li><span className="font-mono">application_member.{'{index}'}.*</span>: application_members（世帯構成）へ転記</li>
              <li><span className="font-mono">application_addition.{'{index}'}.*</span>: application_additions（加算等の申出）へ転記</li>
              <li><span className="font-mono">recipient.*</span> / <span className="font-mono">recipient_detail.*</span>: 受給者テーブルへの直接転記（固定転写の上書き用・通常は不要）</li>
              <li><span className="font-mono">ocr_display.*</span>: OCR確認画面の表示専用</li>
              <li><span className="font-mono">internal.*</span>: OCR確認画面には表示しない（<span className="font-mono">internal.case.applicant_name</span> は案件一覧の申出者氏名抽出パスとして予約）</li>
            </ul>
            <p className="mt-1">
              承認時は受給者（世帯主氏名・宛名・住所・世帯員明細・死亡情報）を固定転写で自動作成し、申出書（applications）と紐づけます。
            </p>
          </div>

          {ocrUseDisabled ? (
            <Alert
              className="mt-4"
              type="warning"
              showIcon
              message="現在、自治体マスタの「OCR利用」は「無」です。マッピングを保存する前に、自治体情報編集で「有」に切り替えることを推奨します。"
            />
          ) : null}
          {error ? <Alert className="mt-4" type="error" showIcon message={error} /> : null}

          <Collapse
            className="mt-4"
            items={[
              {
                key: 'sample',
                label: 'fieldMap V2 サンプル（クリックでコピー）',
                children: (
                  <div>
                    <p className="mb-2 text-xs text-gray-600">
                      「label → {`{ systemKey, ocrPath, section }`}」の形式。配列項目（世帯構成・加算等）は {`{index}`} / {`{number}`} と arrayPath で 1 セットだけ定義します。
                    </p>
                    <pre
                      className="cursor-pointer overflow-x-auto rounded bg-gray-50 p-3 text-xs"
                      onClick={() => copyToClipboard(mappingHelp.fieldMapSample)}
                    >
                      {mappingHelp.fieldMapSample}
                    </pre>
                  </div>
                ),
              },
              {
                key: 'paths',
                label: 'OCR API JSON パス例',
                children: (
                  <div className="flex flex-wrap gap-2">
                    {mappingHelp.ocrPaths.map((p) => (
                      <Tag
                        key={`${p.label}-${p.path}`}
                        className="cursor-pointer"
                        color="blue"
                        onClick={() => copyToClipboard(p.path)}
                      >
                        {p.label}: <span className="font-mono">{p.path}</span>
                      </Tag>
                    ))}
                    {mappingHelp.ocrPaths.length === 0 ? (
                      <span className="text-xs text-gray-500">表示できる OCR path がありません。</span>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'catalog',
                label: `system_key カタログ（${catalog.length} 件）`,
                children: (
                  <div>
                    <Input.Search
                      allowClear
                      placeholder="systemKey / 列名 / ラベルで検索"
                      value={catalogFilter}
                      onChange={(e) => setCatalogFilter(e.target.value)}
                      className="mb-3"
                    />
                    <div className="max-h-96 overflow-y-auto rounded border border-gray-200">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-2 py-1 text-left">systemKey（クリックでコピー）</th>
                            <th className="px-2 py-1 text-left">ラベル</th>
                            <th className="px-2 py-1 text-left">テーブル</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCatalog.map((e) => (
                            <tr key={e.systemKey} className="border-t border-gray-100 hover:bg-blue-50/50">
                              <td
                                className="cursor-pointer px-2 py-1 font-mono text-blue-700"
                                onClick={() => copyToClipboard(e.systemKey)}
                              >
                                {e.systemKey}
                              </td>
                              <td className="px-2 py-1 text-gray-700">{e.defaultLabel}</td>
                              <td className="px-2 py-1 text-gray-500">{e.table}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ),
              },
            ]}
          />

          <label className="mt-4 mb-2 block text-sm font-medium text-gray-700">マッピング定義（JSON）</label>
          <textarea
            value={jsonText}
            onChange={(event) => {
              setJsonText(event.target.value)
              setParseError(null)
            }}
            spellCheck={false}
            rows={22}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm leading-relaxed focus:border-transparent focus:ring-2 focus:ring-violet-500"
          />
          {parseError ? <p className="mt-2 text-sm text-red-600">{parseError}</p> : null}
        </Card>

        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <Button onClick={goToBasicInfoEdit}>キャンセル</Button>
          <Button type="primary" onClick={save} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MunicipalityMoushideshoOcrMapping
